import { CommonModule } from '@angular/common';
import { NgModule, SecurityContext } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbToastModule, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { MarkdownModule } from 'ngx-markdown';
import { NgxMaskModule } from 'ngx-mask';

import { SharedUiUiModule } from '@patient-ui/shared-ui/ui';

import { OrbiAppointmentCardComponent } from './components/orbi-appointment-card/orbi-appointment-card.component';
import { OrbiEntryButtonComponent } from './components/orbi-entry-button/orbi-entry-button.component';
import { OrbiFeedbackComponent } from './components/orbi-feedback/orbi-feedback.component';
import { OrbiFeedbackSheetComponent } from './components/orbi-feedback-sheet/orbi-feedback-sheet.component';
import { OrbiFindABillCardComponent } from './components/orbi-find-a-bill-card/orbi-find-a-bill-card.component';
import { OrbiFindLabCardComponent } from './components/orbi-find-lab-card/orbi-find-lab-card.component';
import { OrbiHistoryItemComponent } from './components/orbi-history-item/orbi-history-item.component';
import { OrbiLiveRegionComponent } from './components/orbi-live-region/orbi-live-region.component';
import { OrbiMessageInputComponent } from './components/orbi-message-input/orbi-message-input.component';
import { OrbiMessageItemComponent } from './components/orbi-message-item/orbi-message-item.component';
import { OrbiPatientPassCardComponent } from './components/orbi-patient-pass-card/orbi-patient-pass-card.component';
import { OrbiResultsCardComponent } from './components/orbi-results-card/orbi-results-card.component';
import { OrbiSuggestedPromptsComponent } from './components/orbi-suggested-prompts/orbi-suggested-prompts.component';
import { OrbiToastsComponent } from './components/orbi-toasts/orbi-toasts.component';
import { OrbiVisitHistoryCardComponent } from './components/orbi-visit-history-card/orbi-visit-history-card.component';
import { OrbiAgentHostComponent } from './containers/orbi-agent-host/orbi-agent-host.component';
import { OrbiHistoryListComponent } from './containers/orbi-history-list/orbi-history-list.component';
import { OrbiPanelComponent } from './containers/orbi-panel/orbi-panel.component';
import { OrbiThreadComponent } from './containers/orbi-thread/orbi-thread.component';

/**
 * Orbi AI assistant UI (Phase 2). Hosts the persistent entry point
 * (PCSA-13157) — an "Ask a question" bar built on the shared message input —
 * the off-canvas chat panel shell (PCSA-13158), the message input +
 * conversation thread (PCSA-13160), the conversation-history view (PCSA-13169),
 * response feedback (PCSA-13171), markdown rendering of agent responses
 * (PCSA-13174), and the structured card renderers — Results (PCSA-13224),
 * Make an Appointment (PCSA-13241), Labcorp Patient Pass (PCSA-13374),
 * Upcoming Appointment (PCSA-13397), Visit History and Find a Bill
 * (PCSA-13419). Unlike the other portal feature modules this is NOT lazy-routed
 * — its components are mounted directly in the SHELL LAYOUTS so they appear on
 * every page a verified patient can reach, authenticated portal pages and the
 * public-layout pages the agent itself links into alike (see
 * `OrbiAgentHostComponent`). Exported so those layouts can use them.
 *
 * MarkdownModule is configured with `sanitize: SecurityContext.HTML` so agent
 * markdown is rendered to HTML but scrubbed of scripts/dangerous markup (XSS
 * protection — the agent text is untrusted LLM output). Flutter rendered native
 * widgets and needed no such step; the web DOM does.
 */
@NgModule({
  declarations: [
    OrbiAgentHostComponent,
    OrbiEntryButtonComponent,
    OrbiPanelComponent,
    OrbiThreadComponent,
    OrbiMessageItemComponent,
    OrbiResultsCardComponent,
    OrbiAppointmentCardComponent,
    OrbiVisitHistoryCardComponent,
    OrbiPatientPassCardComponent,
    OrbiFindLabCardComponent,
    OrbiFindABillCardComponent,
    OrbiMessageInputComponent,
    OrbiHistoryListComponent,
    OrbiHistoryItemComponent,
    OrbiFeedbackComponent,
    OrbiFeedbackSheetComponent,
    OrbiToastsComponent,
    OrbiSuggestedPromptsComponent,
    OrbiLiveRegionComponent,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbToastModule,

    // The Make an Appointment card's ZIP/address suggestions (PCSA-13551).
    NgbTypeaheadModule,
    SharedUiUiModule,
    MarkdownModule.forRoot({ sanitize: SecurityContext.HTML }),
    // `forChild`, not `forRoot`: AppModule already configures ngx-mask globally,
    // and re-rooting here would replace that config for the whole app. The Find a
    // Bill card needs the masked ZIP / date inputs the Billing page uses.
    NgxMaskModule.forChild(),
  ],
  exports: [
    OrbiAgentHostComponent,
    OrbiEntryButtonComponent,
    OrbiPanelComponent,
    OrbiThreadComponent,
    OrbiHistoryListComponent,
  ],
})
export class PortalOrbiModule {}
